import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import groupHealthPlanApi, { GroupHealthPlanPageValues } from 'core/application/adminSignUp/gchra/groupHealthPlan'

import CategoryItemPageHeader from 'common/components/onBoarding/Common'

import { commandRequest } from 'common/RequestUtils'
import { Alert, Col, DefaultSelect, Input, MonthPicker, Notification, Row } from 'common/components'
import { BaseNavigationProps } from 'common/wizard/BaseNavigationProps'

import WizardNextButton from '../../common/WizardNextButton'
import WizardPreviousButton from '../../common/WizardPreviousButton'

import '../../common/CompanyDetailsStyles.less'

const formValidator = (translate: any) =>
  Yup.object({
    groupHealthPlanStartDate: Yup.date()
      .required(translate('general.required'))
      .typeError(translate('general.required')),
    groupHealthPlanName: Yup.string()
      .trim()
      .required(translate('general.required'))
      .typeError(translate('general.required')),
    insuranceCarrier: Yup.string()
      .trim()
      .required(translate('general.required'))
      .typeError(translate('general.required')),
    waitingPeriod: Yup.number().required(translate('general.required')).typeError(translate('general.required')),
  })

const GroupHealthPlan: React.FC<BaseNavigationProps<GroupHealthPlanPageValues>> = (props) => {
  const [waitingPeriodValue, setWaitingPeriodValue] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const { t: translate } = useTranslation()

  const onSubmit = async (values: GroupHealthPlanPageValues) => {
    setLoading(true)
    const res = await commandRequest(() => groupHealthPlanApi({ ...values, waitingPeriod: waitingPeriodValue }))
    setLoading(false)
    if (!res.errors) {
      props.goNext(props.name)
    } else {
      console.log(res.errors)
      Notification.open({
        message: res.errors[0],
        type: 'error',
      })
    }
  }

  const waitingPeriodOptions = [
    {
      id: 0,
      name: 'No waiting Period',
    },
    {
      id: 30,
      name: '30 days',
    },
    {
      id: 60,
      name: '60 days',
    },
    {
      id: 90,
      name: '90 days',
    },
  ]

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={{ ...props.state, waitingPeriod: props.state.waitingPeriod < 0 ? 0 : props.state.waitingPeriod }}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
      }}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} autoComplete="off" className={'t-admin-signup-group-health-plan'}>
          <Row>
            <Col xs={10} offset={2}>
              <CategoryItemPageHeader
                title={translate('admin-signup.company-details.title')}
                numberOfQuestions={5}
                currentQuestion={4}
                question={translate('admin-signup.company-details.health-plan.question')}
              />
            </Col>
          </Row>

          <Row className="form__row">
            <Col xs={10} offset={2}>
              <div className="pl-12">
                <Alert
                  className={'t-admin-signup-group-health-plan-info'}
                  message={translate('admin-signup.company-details.health-plan.alert-title')}
                  description={translate('admin-signup.company-details.health-plan.alert-description')}
                  type="info"
                  showIcon
                />
              </div>
            </Col>
          </Row>

          <Row className="form__row">
            <Col xs={10} lg={10} offset={2} className="">
              <div className="pl-12">
                <MonthPicker
                  label={translate('admin-signup.company-details.health-plan.start-date-label')}
                  name="groupHealthPlanStartDate"
                  defaultValue={
                    props.state.groupHealthPlanStartDate ? moment(props.state.groupHealthPlanStartDate) : undefined
                  }
                  size="large"
                  disabledDate={(d) =>
                    !d ||
                    d.isBefore(moment().subtract(10, 'month').subtract(1, 'day')) ||
                    d.isAfter(moment().add(5, 'month'))
                  }
                  id="groupHealthPlanStartDate"
                  format="MM-YYYY"
                  placeholder=""
                />
              </div>
            </Col>
          </Row>

          <Row className="form__row">
            <Col xs={10} offset={2}>
              <div className="pl-12">
                <h5 className={'t-admin-signup-group-health-carrier-question'}>
                  {translate('admin-signup.company-details.health-plan.insurance-carrier-question')}
                </h5>
              </div>
            </Col>
          </Row>

          <Row className="form__row">
            <Col xs={10} offset={2}>
              <Input
                label={translate('admin-signup.company-details.health-plan.insurance-carrier-label')}
                name="insuranceCarrier"
                placeholder={translate('admin-signup.company-details.health-plan.insurance-carrier-placeholder')}
              />
            </Col>
          </Row>

          <Row className="form__row ">
            <Col xs={10} offset={2}>
              <div className="pl-12">
                <h5 className={'t-admin-signup-group-health-plan-name-question'}>
                  {translate('admin-signup.company-details.health-plan.insurance-plan-name-qestion')}
                </h5>
                <label>{translate('admin-signup.company-details.health-plan.insurance-plan-name-qestion-hint')}</label>
              </div>
            </Col>
          </Row>

          <Row className="form__row">
            <Col span={10} offset={2}>
              <Input
                label={translate('admin-signup.company-details.health-plan.insurance-plan-label')}
                name="groupHealthPlanName"
                placeholder={translate('admin-signup.company-details.health-plan.insurance-plan-placeholder')}
              />
            </Col>
          </Row>

          <Row className="form__row ">
            <Col xs={10} offset={2}>
              <div className="pl-12">
                <h5 className={'t-admin-signup-group-health-waiting-period-question'}>
                  {translate('admin-signup.company-details.health-plan.waiting-period-question')}
                </h5>
              </div>
            </Col>
          </Row>

          <Row className="form__row ml-12">
            <Col xs={8} offset={2}>
              <DefaultSelect
                name="waitingPeriod"
                onChange={(e) => {
                  setWaitingPeriodValue(e)
                }}
                className="width_100"
                items={waitingPeriodOptions}
              ></DefaultSelect>
            </Col>
          </Row>

          <Row className="bottom__actions">
            <Col xs={20} offset={2}>
              <WizardPreviousButton
                onClick={() => {
                  props.goBack(props.name)
                }}
              />

              <WizardNextButton disabled={!formValidator(translate).isValidSync(formik.values)} loading={loading} />
            </Col>
          </Row>
        </form>
      )}
    </Formik>
  )
}

export default GroupHealthPlan
